import { useDeviceStore } from '@/stores';
import { track } from './../sendData';

interface TrackData {
  [key: string]: unknown;
}

// 数据缓存key
const CACHE_KEY = 'cacheTrackData';
// 最大缓存数
const DEFAULT_MAX_NUM = 100;
// 上报间隔
const REPORT_INTERVAL = 2000; // 2秒
// 有效曝光时间
const EXPOSURE_DURATION = 300; // 300毫秒
// 上报间隔退避上限
const MAX_REPORT_INTERVAL = 30000; // 30秒
// 单条数据本次会话累计失败次数上限，达到后本次会话不再重试，
// 但数据仍保存在缓存中，等待下次进入页面重新获得重试机会
const MAX_RETRY = 5;
// 内部重试计数字段（上报前剥离，不会发给服务端）
const RETRY_KEY = '__retry';

export default class Exposure {
  constructor(
    maxExposureCount = DEFAULT_MAX_NUM,
    duration = EXPOSURE_DURATION
  ) {
    this.maxExposureCount = maxExposureCount;
    this.duration = duration;
    this.init();
  }

  private exposureData: TrackData[] = [];
  private maxExposureCount = 0;
  private timer = 0;
  private duration = 0;
  private observer: IntersectionObserver | null = null;
  // 当前上报间隔，失败时翻倍退避（上限 {@link MAX_REPORT_INTERVAL}），成功后恢复
  private reportInterval = REPORT_INTERVAL;
  // 下次允许发送的最早时间（退避门闩，定时器与观察者攒批两条触发路径共用）
  private nextSendAt = 0;

  /**
   * 初始化：先上报上次退出遗留的缓存数据（见 {@link loadCacheData}），
   * 然后按当前间隔周期上报（失败后间隔翻倍退避，成功恢复）
   */
  init() {
    this.loadCacheData();
    this.tick();

    // 实例化监听
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // 出现在视窗中
          if (entry.isIntersecting) {
            // 清除当前定时器
            clearTimeout(Number(entry.target.getAttribute('timer')));
            entry.target.setAttribute('start', Date.now().toString());
            // console.log('enter', entry, entry.target.getAttribute('start'));
            // 获取参数
            const tp = entry.target.getAttribute('track-params') || '';
            const deviceStore = useDeviceStore();
            let params = null;
            try {
              params = JSON.parse(tp);
              params.create_time = Date.now(); // 更新曝光时间
              params.network = deviceStore.network; // 更新网络环境
              params.device_id = deviceStore.getDeviceId(); // 更新设备id
            } catch {
              params = null;
            }
            if (!params) {
              return;
            }
            const timer = setTimeout(() => {
              // console.log('setTimeout');
              // 收集参数统一上报，减少网络请求
              this.exposureData.push(params);

              if (this.exposureData.length >= this.maxExposureCount) {
                // 攒满一批立即触发上报（同样受退避门闩约束）
                this.track();
              }
              if (this.exposureData.length > 0) {
                this.storeIntoLocalStorage(JSON.stringify(this.exposureData));
              }
            }, this.duration);
            entry.target.setAttribute('timer', timer.toString());
          } else {
            // console.log(
            //   'leave',
            //   entry,
            //   Date.now(),
            //   entry.target.getAttribute('start')
            // );
            clearTimeout(Number(entry.target.getAttribute('timer')));
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.5,
      }
    );
  }

  /**
   * 给元素添加监听
   * @param {Element} entry
   */
  add(entry: { el: Element }) {
    this.observer?.observe(entry.el);
  }

  /**
   * 判断该条数据本次会话是否已耗尽重试次数（耗尽的仅保存、不再发送）
   * @param {TrackData} item 数据条目
   * @return {boolean} 是否已耗尽
   */
  private isRetryExhausted(item: TrackData): boolean {
    // 计数可能被不可信缓存污染成非数字，严格校验，非法值视为未重试过
    const raw = item[RETRY_KEY];
    return typeof raw === 'number' && raw >= MAX_RETRY;
  }

  /**
   * 埋点上报
   * 定时器与观察者攒批两条路径共用，退避期间（{@link nextSendAt} 之前）不发送；
   * 失败时按条累计重试次数，达到 {@link MAX_RETRY} 次的本次会话不再重试，
   * 但仍保留在队列与缓存中（不丢弃），下次进入页面重新参与上报
   */
  track() {
    // 退避门闩：故障期间两条触发路径都不会提前重试
    if (Date.now() < this.nextSendAt) {
      return;
    }
    // 挑选批次：跳过已耗尽重试的条目（它们留在队列中仅保存）
    const trackData: TrackData[] = [];
    const remaining: TrackData[] = [];
    this.exposureData.forEach((item) => {
      if (
        trackData.length < this.maxExposureCount &&
        !this.isRetryExhausted(item)
      ) {
        trackData.push(item);
      } else {
        remaining.push(item);
      }
    });
    if (trackData.length <= 0) {
      // 队列中只剩已耗尽重试的数据，本次会话不再发送，数据仍保存在缓存中
      return;
    }
    this.exposureData = remaining;
    this.nextSendAt = Date.now() + this.reportInterval;
    console.log(`Exposure data to server: `, trackData);
    // 剥离内部重试计数，不发给服务端
    const payload = trackData.map((item) => {
      const rest = { ...item };
      delete rest[RETRY_KEY];
      return rest;
    });
    track({
      addData: JSON.stringify(payload),
    })
      .then(() => {
        // 上报成功，若此前处于退避则立即恢复正常间隔并重新调度
        if (this.reportInterval !== REPORT_INTERVAL) {
          this.reportInterval = REPORT_INTERVAL;
          this.nextSendAt = Date.now() + REPORT_INTERVAL;
          this.scheduleNext();
        }
      })
      .catch(() => {
        // 每条数据独立累计失败次数并全部重新入队（不丢弃）；
        // 达到上限的条目留在队列中但会被 {@link track} 跳过，仅保存不再发送
        trackData.forEach((item) => {
          const raw = item[RETRY_KEY];
          const retry = typeof raw === 'number' && raw > 0 ? raw : 0;
          item[RETRY_KEY] = retry + 1;
        });
        this.exposureData = [...trackData, ...this.exposureData];
        this.storeIntoLocalStorage(JSON.stringify(this.exposureData));
        // 失败退避：间隔翻倍、更新门闩并立即重新调度
        this.reportInterval = Math.min(
          this.reportInterval * 2,
          MAX_REPORT_INTERVAL
        );
        this.nextSendAt = Date.now() + this.reportInterval;
        this.scheduleNext();
      });
    // 更新localStorage
    this.storeIntoLocalStorage(JSON.stringify(this.exposureData));
  }

  /**
   * 存储到localstorage, 防止在设定上报时间内用户退出
   * @param { string } data
   */
  storeIntoLocalStorage(data: string) {
    try {
      window.localStorage.setItem(CACHE_KEY, data);
    } catch {
      // 存储不可用（隐私模式/配额耗尽）时静默降级，仅保留内存队列
    }
  }

  /**
   * 读取上次退出未上报的缓存数据，作为初始待上报队列
   * 缓存数据中 device_id 为空时自动补齐，避免上报空值
   * 注意：有效数据不在此处删除，发送时 track() 会用剩余数据重写缓存完成清理，
   * 提前删除会导致解析或上报失败时数据永久丢失
   */
  loadCacheData() {
    let cache: string | null = null;
    try {
      cache = window.localStorage.getItem(CACHE_KEY);
    } catch {
      // 存储不可访问（沙箱iframe/隐私模式等读取即抛错），按无缓存降级
      return;
    }
    if (!cache) {
      return;
    }
    let cacheData: unknown;
    try {
      cacheData = JSON.parse(cache);
    } catch {
      console.warn(`曝光缓存数据解析失败，已忽略: ${cache.slice(0, 200)}`);
      this.removeCacheData();
      return;
    }
    if (!Array.isArray(cacheData)) {
      this.removeCacheData();
      return;
    }
    // 过滤非对象与数组元素：对原始值赋属性会抛错；
    // 数组元素即使补了 device_id，JSON.stringify 时也会丢失
    const list = (cacheData as unknown[]).filter(
      (item): item is TrackData =>
        typeof item === 'object' && item !== null && !Array.isArray(item)
    );
    if (list.length <= 0) {
      this.removeCacheData();
      return;
    }
    // 每次进入页面给一轮新的重试预算（重置上次会话耗尽的计数），
    // 并补齐缺失的 device_id（整批共用同一设备号）
    const needsDeviceId = list.some((item) => !item.device_id);
    const deviceId = needsDeviceId ? useDeviceStore().getDeviceId() : '';
    list.forEach((item) => {
      delete item[RETRY_KEY];
      if (needsDeviceId && !item.device_id) {
        item.device_id = deviceId;
      }
    });
    this.exposureData = list;
  }

  /**
   * 删除缓存数据（存储不可用时静默忽略）
   */
  private removeCacheData() {
    try {
      window.localStorage.removeItem(CACHE_KEY);
    } catch {
      // 存储不可用，静默忽略
    }
  }

  /**
   * 执行一次上报并无条件续排下一次，
   * 保证单次上报的同步异常不会永久中断调度链
   */
  private tick() {
    try {
      this.track();
    } finally {
      this.scheduleNext();
    }
  }

  /**
   * 按当前间隔安排下一次上报；
   * 间隔变化（退避/恢复）时由 {@link track} 调用本方法立即重新调度
   */
  private scheduleNext() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.tick();
    }, this.reportInterval) as unknown as number;
  }
}
